import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import { api, useMock } from '@/lib/api'

const STORAGE_KEY = 'sv:prefs'

export type Language = 'th' | 'en'
export type Theme    = 'dark' | 'light' | 'system'
export type Region   = 'us' | 'gb' | 'de' | 'jp' | 'hk' | 'ca' | 'au' | 'fr' | 'in' | 'br' | 'sg' | 'kr' | 'tw' | 'ch'

interface Prefs {
  language: Language
  theme: Theme
  notifTelegram: boolean
  notifInapp: boolean
  regions: Region[]
}

const DEFAULT: Prefs = {
  language: 'th',
  theme: 'dark',
  notifTelegram: true,
  notifInapp: true,
  regions: ['us'],
}

function load(): Prefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? { ...DEFAULT, ...JSON.parse(raw) } : { ...DEFAULT }
  } catch {
    return { ...DEFAULT }
  }
}

export const usePrefsStore = defineStore('prefs', () => {
  const initial = load()
  const language      = ref<Language>(initial.language)
  const theme         = ref<Theme>(initial.theme)
  const notifTelegram = ref<boolean>(initial.notifTelegram)
  const notifInapp    = ref<boolean>(initial.notifInapp)
  const regions       = ref<Region[]>(initial.regions?.length ? [...initial.regions] : ['us'])

  function persist() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        language: language.value,
        theme: theme.value,
        notifTelegram: notifTelegram.value,
        notifInapp: notifInapp.value,
        regions: regions.value,
      }))
    } catch { /* ignore quota errors */ }
  }

  let syncing = false
  async function syncToServer() {
    if (useMock || syncing) return
    syncing = true
    try {
      await api.patch('/api/preferences', {
        language: language.value,
        theme: theme.value,
        notif_telegram: notifTelegram.value ? 1 : 0,
        notif_inapp:    notifInapp.value    ? 1 : 0,
        regions: regions.value,
      })
    } catch (e) {
      console.warn('prefs sync failed', e)
    } finally {
      syncing = false
    }
  }

  async function loadFromServer() {
    if (useMock) return
    try {
      const { data } = await api.get('/api/preferences')
      if (data?.language) language.value = data.language
      if (data?.theme)    theme.value    = data.theme
      if (typeof data?.notif_telegram === 'number') notifTelegram.value = data.notif_telegram === 1
      if (typeof data?.notif_inapp    === 'number') notifInapp.value    = data.notif_inapp    === 1
      if (Array.isArray(data?.regions) && data.regions.length) regions.value = data.regions
      persist()
    } catch (e) {
      console.warn('prefs load failed', e)
    }
  }

  watch([language, theme, notifTelegram, notifInapp, regions], () => {
    persist()
    void syncToServer()
  }, { deep: true })

  return { language, theme, notifTelegram, notifInapp, regions, loadFromServer }
})
